import { useCallback, useEffect, useState } from "react";
import {
  fetchPushPublicKey,
  fetchPushSubscriptions,
  PushSubscriptionRow,
  sendTestPush,
  subscribePush,
  unsubscribePush,
  unsubscribePushByEndpoint,
} from "../api/pushApi";

export type PushPermission = NotificationPermission | "unsupported";

export interface UsePushSubscriptionResult {
  /** Browser-level support flag — false on legacy iOS Safari, http origins, etc. */
  isSupported: boolean;
  /** Backend RecaptchaGuard-style flag — true once VAPID keys are configured. */
  isBackendEnabled: boolean;
  permission: PushPermission;
  /** Whether THIS browser is currently subscribed (we cross-check the SW). */
  isSubscribed: boolean;
  /** All devices the current user has subscribed (across browsers). */
  devices: PushSubscriptionRow[];
  loading: boolean;
  error: string | null;

  /** Request permission + subscribe + register with backend. */
  enable: () => Promise<void>;
  /** Unsubscribe locally + delete from backend. */
  disable: () => Promise<void>;
  /** Trigger a test push from the backend to verify end-to-end delivery. */
  sendTest: () => Promise<void>;
  /** Remove a specific device by id (eg. "Chrome on Pixel"). */
  removeDevice: (id: string) => Promise<void>;
}

const isPushSupported = (): boolean =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export function usePushSubscription(): UsePushSubscriptionResult {
  const supported = isPushSupported();
  const [isBackendEnabled, setBackendEnabled] = useState<boolean>(false);
  const [permission, setPermission] = useState<PushPermission>(
    supported ? Notification.permission : "unsupported",
  );
  const [isSubscribed, setSubscribed] = useState<boolean>(false);
  const [devices, setDevices] = useState<PushSubscriptionRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!supported) return;
    try {
      const [{ enabled }, list] = await Promise.all([
        fetchPushPublicKey(),
        fetchPushSubscriptions(),
      ]);
      setBackendEnabled(enabled);
      setDevices(list);

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(Boolean(sub));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [supported]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const enable = useCallback(async () => {
    if (!supported) {
      setError("Tu navegador no soporta web push.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { publicKey, enabled } = await fetchPushPublicKey();
      if (!enabled || !publicKey) {
        throw new Error(
          "El backend no tiene VAPID keys configuradas. Avisale al admin.",
        );
      }

      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        throw new Error("Permiso denegado por el navegador.");
      }

      const reg = await navigator.serviceWorker.ready;
      let subscription = await reg.pushManager.getSubscription();
      if (!subscription) {
        subscription = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }

      const json = subscription.toJSON();
      const p256dh = json.keys?.p256dh;
      const auth = json.keys?.auth;
      if (!subscription.endpoint || !p256dh || !auth) {
        throw new Error(
          "Subscription incompleta — el navegador no devolvió las claves.",
        );
      }

      await subscribePush({
        endpoint: subscription.endpoint,
        p256dh,
        auth,
      });

      setSubscribed(true);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [supported, refresh]);

  const disable = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    setError(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.getSubscription();
      if (subscription) {
        // Las claves se leen ANTES de cancelar: después, la suscripción ya no las
        // da. Identifican ESTA suscripción, así que si otra pestaña volvió a
        // suscribirse mientras tanto, el backend no borra la nueva.
        const json = subscription.toJSON();
        const endpoint = subscription.endpoint;
        const p256dh = json.keys?.p256dh;
        const auth = json.keys?.auth;

        // El backend PRIMERO, y recién después la baja local. Al revés, un POST
        // que fallara (red, 500) dejaba la suscripción local ya cancelada: el
        // clic siguiente encontraba `getSubscription()` en null, se salteaba
        // este bloque entero y no reintentaba nada, así que la fila quedaba
        // viva hasta que algún push futuro devolviera 410. En este orden, un
        // fallo del POST deja todo como estaba y el reintento sirve.
        //
        // Por ENDPOINT + claves, no por el id de la lista: durante la migración la
        // fila de este navegador todavía puede ser del USUARIO, así que no está en
        // `devices`. Agarrar la primera de la lista (lo que se hacía antes) le
        // borraba al admin OTRO dispositivo y dejaba la fila vieja viva.
        if (p256dh && auth) {
          await unsubscribePushByEndpoint({ endpoint, p256dh, auth });
        }

        // Si esto falla con la fila ya borrada, el alta siguiente vuelve a
        // suscribir y a mandar la suscripción: se arregla solo, al revés no.
        await subscription.unsubscribe();
      }
      setSubscribed(false);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [supported, devices, refresh]);

  const sendTest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await sendTestPush();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const removeDevice = useCallback(
    async (id: string) => {
      setLoading(true);
      setError(null);
      try {
        await unsubscribePush(id);
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    },
    [refresh],
  );

  return {
    isSupported: supported,
    isBackendEnabled,
    permission,
    isSubscribed,
    devices,
    loading,
    error,
    enable,
    disable,
    sendTest,
    removeDevice,
  };
}
